import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { DividerModule } from 'primeng/divider';
import { RippleModule } from 'primeng/ripple';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../../service/auth.service';
import { authConfig } from '../../../../config/auth.config';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ButtonModule, CardModule, ToggleButtonModule, DividerModule, RippleModule, FormsModule],
    template: `
        <div class="login-container">
            <div class="login-background"></div>
            <p-card class="login-card">
                <div class="login-header">
                    <div class="logo-container">
                        <img src="/demo/images/ZilonLogo_big.svg" alt="Zilon Logo" class="logo" />
                    </div>
                    <!-- <h1 class="app-title">Zilon</h1>  -->
                    <h2 class="app-subtitle">Task Management System</h2>
                    <p class="welcome-text">Welcome back! Please sign in to continue</p>
                </div>
                
                <div class="login-content">
                    <!-- Environment Mode Toggle (Development Only) -->
                    <div *ngIf="!authConfig.production" class="auth-mode-toggle">
                        <label class="toggle-label">Authentication Mode:</label>
                        <p-toggleButton 
                            [(ngModel)]="useMockAuth" 
                            onLabel="Demo Mode" 
                            offLabel="Office365"
                            (onChange)="onAuthModeChange()"
                            styleClass="auth-toggle">
                        </p-toggleButton>
                        <small class="toggle-description">
                            {{ useMockAuth ? 'Testing with mock users' : 'Live Office365 authentication' }}
                        </small>
                    </div>
                    
                    <p-divider *ngIf="!authConfig.production"></p-divider>

                    <div *ngIf="useMockAuth" class="mock-auth-section">
                        <div class="demo-badge">
                            <i class="pi pi-info-circle"></i>
                            Demo Mode - Choose your role to continue
                        </div>
                        
                        <div class="login-buttons">
                            <p-button 
                                label="Sign in as Manager" 
                                icon="pi pi-crown"
                                (onClick)="loginAsMockManager()"
                                [loading]="isLoading"
                                styleClass="p-button-lg manager-btn">
                            </p-button>
                            
                            <p-button 
                                label="Sign in as Employee" 
                                icon="pi pi-users"
                                (onClick)="loginAsMockEmployee()"
                                [loading]="isLoading"
                                severity="secondary"
                                styleClass="p-button-lg employee-btn">
                            </p-button>
                        </div>
                    </div>

                    <div *ngIf="!useMockAuth" class="office365-auth-section">
                        <div class="office-info">
                            <i class="pi pi-shield"></i>
                            <p>Secure sign-in with your Office365 account</p>
                        </div>
                        
                        <div class="login-buttons">
                            <p-button 
                                label="Sign in with Microsoft" 
                                icon="pi pi-microsoft"
                                (onClick)="loginWithPopup()"
                                [loading]="isLoading"
                                styleClass="p-button-lg microsoft-btn">
                            </p-button>
                            
                            <p-button 
                                label="Alternative Sign-in" 
                                icon="pi pi-sign-in"
                                (onClick)="loginWithRedirect()"
                                severity="secondary"
                                outlined="true"
                                styleClass="p-button-lg redirect-btn">
                            </p-button>
                        </div>
                    </div>
                </div>
                
                <div class="login-footer">
                    <small>© 2025 Zilon Task Management. All rights reserved.</small>
                </div>
            </p-card>
        </div>
    `,
    styles: [`
        .login-container {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            padding: 2rem;
            overflow: hidden;
        }
        
        .login-background {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
            z-index: -2;
        }
        
        .login-background::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.05"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.05"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.05"/></pattern></defs><rect width="100%" height="100%" fill="url(%23grain)"/></svg>');
            z-index: -1;
        }
        
        .login-card {
            position: relative;
            z-index: 1;
        }
        
        .login-header {
            text-align: center;
            padding: 2rem 2rem 1rem 2rem;
            border-bottom: 1px solid var(--surface-border);
            margin-bottom: 2rem;
        }
        
        .logo-container {
            margin-bottom: 1.5rem;
        }
        
        .logo {
            width: 80px;
            height: 80px;
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
            animation: logoFloat 3s ease-in-out infinite;
        }
        
        @keyframes logoFloat {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
        }
        
        .app-title {
            font-size: 2.5rem;
            font-weight: 700;
            color: var(--primary-color);
            margin: 0.5rem 0;
            background: linear-gradient(45deg, var(--primary-color), var(--primary-600));
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
        
        .app-subtitle {
            font-size: 1.2rem;
            font-weight: 400;
            color: var(--text-color-secondary);
            margin: 0.5rem 0;
        }
        
        .welcome-text {
            color: var(--text-color-secondary);
            font-size: 0.95rem;
            margin: 1rem 0 0 0;
        }
        
        .login-content {
            padding: 0 2rem 2rem 2rem;
        }
        
        .auth-mode-toggle {
            background: var(--surface-100);
            padding: 1.5rem;
            border-radius: 12px;
            margin-bottom: 1rem;
            text-align: center;
            border: 1px solid var(--surface-200);
        }
        
        .toggle-label {
            display: block;
            font-weight: 600;
            color: var(--text-color);
            margin-bottom: 1rem;
            font-size: 1rem;
        }
        
        .toggle-description {
            display: block;
            color: var(--text-color-secondary);
            margin-top: 0.5rem;
            font-style: italic;
        }
        
        .demo-badge {
            background: linear-gradient(45deg, #ffd89b 0%, #19547b 100%);
            color: white;
            padding: 1rem;
            border-radius: 12px;
            margin-bottom: 2rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-weight: 500;
            box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }
        
        .office-info {
            background: var(--surface-50);
            padding: 1rem;
            border-radius: 12px;
            margin-bottom: 2rem;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            border: 1px solid var(--surface-200);
        }
        
        .office-info i {
            color: var(--primary-color);
            font-size: 1.2rem;
        }
        
        .office-info p {
            margin: 0;
            color: var(--text-color);
            font-weight: 500;
        }
        
        .login-buttons {
            display: flex;
            flex-direction: column;
            gap: 1rem;
        }
        
        .login-footer {
            text-align: center;
            padding: 1rem 2rem;
            border-top: 1px solid var(--surface-border);
            background: var(--surface-50);
        }
        
        .login-footer small {
            color: var(--text-color-secondary);
        }
        
        :host ::ng-deep .login-card.p-card {
            width: 100%;
            max-width: 450px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.15);
            border-radius: 20px;
            overflow: hidden;
            backdrop-filter: blur(10px);
            background: rgba(255, 255, 255, 0.95);
            border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        :host ::ng-deep .login-card .p-card-body {
            padding: 0;
        }
        
        :host ::ng-deep .p-button {
            width: 100%;
            padding: 1rem;
            font-weight: 600;
            border-radius: 12px;
            transition: all 0.3s ease;
        }
        
        :host ::ng-deep .manager-btn {
            background: linear-gradient(45deg, #667eea, #764ba2) !important;
            border: none !important;
        }
        
        :host ::ng-deep .manager-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4) !important;
        }
        
        :host ::ng-deep .employee-btn {
            background: linear-gradient(45deg, #f093fb, #f5576c) !important;
            border: none !important;
            color: white !important;
        }
        
        :host ::ng-deep .employee-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(240, 147, 251, 0.4) !important;
        }
        
        :host ::ng-deep .microsoft-btn {
            background: linear-gradient(45deg, #0078d4, #106ebe) !important;
            border: none !important;
        }
        
        :host ::ng-deep .microsoft-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 120, 212, 0.4) !important;
        }
        
        :host ::ng-deep .redirect-btn {
            border: 2px solid var(--primary-color) !important;
            background: transparent !important;
            color: var(--primary-color) !important;
        }
        
        :host ::ng-deep .redirect-btn:hover {
            background: var(--primary-color) !important;
            color: white !important;
            transform: translateY(-2px);
        }
        
        :host ::ng-deep .p-togglebutton {
            width: 100%;
            border-radius: 12px;
            font-weight: 500;
            padding: 0.75rem 1.5rem;
            transition: all 0.3s ease;
        }
        
        :host ::ng-deep .p-togglebutton.p-highlight {
            background: linear-gradient(45deg, var(--primary-color), var(--primary-600)) !important;
            border-color: var(--primary-color) !important;
            transform: scale(1.02);
            box-shadow: 0 4px 15px rgba(var(--primary-color-rgb), 0.3);
        }
        
        :host ::ng-deep .auth-toggle {
            max-width: 200px;
            margin: 0 auto;
        }
        
        @media (max-width: 768px) {
            .login-container {
                padding: 1rem;
            }
            
            .login-header {
                padding: 1.5rem 1.5rem 1rem 1.5rem;
            }
            
            .login-content {
                padding: 0 1.5rem 1.5rem 1.5rem;
            }
            
            .app-title {
                font-size: 2rem;
            }
            
            .logo {
                width: 60px;
                height: 60px;
            }
            
            :host ::ng-deep .login-card.p-card {
                max-width: 100%;
                margin: 0;
            }
        }
        
        @media (max-width: 480px) {
            .login-container {
                padding: 0.5rem;
            }
            
            .app-title {
                font-size: 1.8rem;
            }
            
            .app-subtitle {
                font-size: 1rem;
            }
        }
    `]
})
export class LoginComponent {
    private authService = inject(AuthService);
    
    isLoading = false;
    useMockAuth = authConfig.useMockAuth;
    authConfig = authConfig;

    async loginWithPopup() {
        this.isLoading = true;
        try {
            await this.authService.loginWithOffice365();
        } finally {
            this.isLoading = false;
        }
    }

    async loginWithRedirect() {
        await this.authService.loginRedirect();
    }

    async loginAsMockManager() {
        this.isLoading = true;
        try {
            this.authService.switchToManagerMode();
            await new Promise(resolve => setTimeout(resolve, 500)); // Simulate loading
        } finally {
            this.isLoading = false;
        }
    }

    async loginAsMockEmployee() {
        this.isLoading = true;
        try {
            this.authService.switchToEmployeeMode();
            await new Promise(resolve => setTimeout(resolve, 500)); // Simulate loading
        } finally {
            this.isLoading = false;
        }
    }

    onAuthModeChange() {
        // Update config setting (for demo purposes)
        authConfig.useMockAuth = this.useMockAuth;
        
        if (this.useMockAuth) {
            // Switch to mock auth and auto-login as manager
            this.authService.switchToManagerMode();
        } else {
            // Clear mock auth
            this.authService.logout();
        }
    }
}